import { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowCircleLeft";
// import { IUnitsInformation } from "../components/UnitsInformation";
import { useUnitInformation } from "../context/UnitsInformationProvider";
import { useConfig } from "../context/ConfigProvider";
import { UnitGroup } from "../types/UnitGroup";
import { getAllImages } from "../utils/UnitImages";
import { GetUnitGroupSummary } from "../services";
import { getFacilityAlias } from "../utils/Aliasing";
import { createUnitsGroup } from "../components/UnitsInformation/type";
import { InformationPair } from "../components/InformationPair";
import formatFloorData from "../utils/FormatFloor";
import Loader from "../components/loader";
// import { getProperValue } from "../components/UnitsInformation/subComponents/Pricing";
import { ReservationInformation } from "./ReservationForm/ReservationInformation";

interface IReservationForm {
  onBackClick: () => void;
}

export const ConfirmationScreen: FC<IReservationForm> = (props) => {
  const {
    reservation,
    reservationRequest,
    navigateToOMI,
    clearReservation,
    unitGroups,
    isLoadingUnitGroups,
  } = useUnitInformation();
  const { widgetParams, configuration } = useConfig();
  const [unitGroupSummary, setUnitGroupSummary] = useState<
    UnitGroup | undefined
  >();

  const selectedUnit = useMemo(() => {
    const unitGroup = unitGroups.find(
      (ug) => ug.id === reservation?.unitGroupId,
    );
    return unitGroup ? createUnitsGroup(unitGroup, configuration) : undefined;
  }, [widgetParams.reservationId, unitGroups, reservation, configuration]);

  const getImageSet = selectedUnit
    ? getAllImages(selectedUnit?.images, selectedUnit)
    : [];

  useEffect(() => {
    if (!selectedUnit) return;
    GetUnitGroupSummary(
      widgetParams.accessKey,
      getFacilityAlias(widgetParams, true),
      selectedUnit.id,
    ).then((resp) => {
      setUnitGroupSummary(resp);
    });
  }, [widgetParams, selectedUnit?.id]);

  if (isLoadingUnitGroups) {
    return (
      <Box width="100%" alignContent="center">
        <Loader />
      </Box>
    );
  }
  return (
    <div>
      <Box
        sx={{
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <Button
          sx={{ padding: 0, marginLeft: -1.25 }}
          onClick={() => {
            props.onBackClick();
            clearReservation();
          }}
        >
          <ArrowBackIcon />
          Back
        </Button>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Typography variant="h5" gutterBottom>
            {selectedUnit?.name ||
              `${selectedUnit?.size.length}’ x ${selectedUnit?.size.width}’`}
          </Typography>
          <InformationPair
            data={[
              {
                label: "Size",
                value: `${selectedUnit?.size?.width}ft x ${selectedUnit?.size?.length}ft (${selectedUnit?.area} sq ft)`,
              },
              {
                label: "Amenities",
                value: (selectedUnit?.amenities || []).join(", "),
              },
              {
                label: "Location",
                value: [
                  unitGroupSummary?.accessType,
                  formatFloorData(unitGroupSummary?.floor),
                  unitGroupSummary?.unitEnclosureStatus,
                ]
                  .filter((x) => Boolean(x))
                  .join(", "),
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <Box display="flex" flexDirection="row" flexWrap="wrap">
            {getImageSet.map((image, index) => (
              <Box
                key={index}
                sx={(theme) => ({
                  height: theme.spacing(16),
                  width: theme.spacing(16),
                  margin: theme.spacing(1),
                })}
              >
                <img
                  src={image || "https://placehold.co/150"}
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "contain",
                  }}
                  alt={selectedUnit?.name || "Unit Group Image"}
                />
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
      {reservation && reservationRequest && (
        <ReservationInformation
          typeOfForm="reservation"
          reservationInformation={reservation}
          reservationRequest={reservationRequest}
          onMoveInClick={() => navigateToOMI(reservation, true)}
          onFindAnotherUnitClick={() => {
            props.onBackClick();
            clearReservation();
          }}
        />
      )}
    </div>
  );
};
